import React from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import {
  FeatureTypeGroup,
  CustomFeatureTypeGroupAffiliation,
} from '../../../../../api/types';
import { Feedback } from "../../../../../components/Validator";
import SortableContainer from '../../../../../components/SortableContainer';
import Badge from "../../../../../components/Badge";

import { disableIfIntroducedInPreviousVersionOrOverride } from '../../../../../components/disableInputPolicy';


const assignId = 'assignSelectFeatureTypeGroups';
const defaultId = 'defaultSelectFeatureTypeGroup';

interface AssignToFeatureTypeGroupsProps {
  default: string
  groups: CustomFeatureTypeGroupAffiliation[]
  featureTypeGroups: FeatureTypeGroup[]
  currentVersion: number
  setGroups: (
    e: any
  ) => void
  setDefault: (
    e: any
  ) => void
  onRemove: (
    id: number
  ) => void
  valid: Feedback
  disabled: boolean
}

const AssignToFeatureTypeGroups = ( props: AssignToFeatureTypeGroupsProps ) => {
  const disabledAssignFtg = props.disabled || !props.valid.valid

  const renderSortable = ( item: CustomFeatureTypeGroupAffiliation ) => {
    const featureTypeGroup = props.featureTypeGroups.find(ftg => ftg.id === item.groupId);

    const onRemove = () => props.onRemove(item.groupId);

    const versionDisabled = disableIfIntroducedInPreviousVersionOrOverride(
      {
        introducedInVersion: item.introducedInVersion!,
      },
      props.currentVersion,
      false
    )

    return (
      <Badge
        className='m-1'
        variant={item.primary ? 'primary' : 'secondary'}
        pill={true}
        outline={versionDisabled}
        disabled={disabledAssignFtg}
        rightIcon={versionDisabled
          ? <i className='modus-icons'>lock</i>
          : <i className='modus-icons' onClick={onRemove}>circle_close</i>
        }
        rightIconClassName={versionDisabled ? '' : 'cursor-pointer'}
      >
        {featureTypeGroup!.name}
      </Badge>
    )
  };

  const renderFeatureTypeGroupsOptions = () => props.featureTypeGroups.map((ftg, i) => {
    const group = props.groups.find(g => g.groupId === ftg.id);
    return (
      <option
        key={i}
        value={ftg.id}
        disabled={!group ? false : true}
      >
        {ftg.name}
      </option>
    );
  });

  const renderDefaultOptions = () => props.groups.map((g, i) => {
    const featureTypeGroup = props.featureTypeGroups.find(ftg => ftg.id === g.groupId);
    return featureTypeGroup?.name ? ( 
      <option
        key={i}
        value={featureTypeGroup.id}
      >
        {featureTypeGroup.name}
      </option>
    ) : null;
  });

  return (
    <React.Fragment>
      {props.groups && props.groups.length > 0 ? (
        <Form.Group className='mb-4'>
          <SortableContainer
            className='border rounded p-2'
            items={props.groups}
            renderElement={renderSortable}
            disabled={true}
          />
          <Form.Text>
            Remove an unlocked feature type group association by clicking the button.
          </Form.Text>
        </Form.Group>
      ) : null}
      
      <Form.Group as={Row} controlId={assignId}>
        <Form.Label column={true} md='3'>Groups</Form.Label>
        <Col md='9'>
          <Form.Control
            as='select'
            // className='custom-select'
            value=''
            onChange={props.setGroups}
            disabled={disabledAssignFtg}
          >
            <option value='' disabled={true}>-- please select --</option>
            {renderFeatureTypeGroupsOptions()}
          </Form.Control>
          <Form.Text>
            Assign the new feature type to one or more feature type groups.
          </Form.Text>
        </Col>
      </Form.Group>
      
      <Form.Group as={Row} controlId={defaultId}>
        <Form.Label column={true} md='3'>
          Default{' '}<span className="text-danger">*</span>
        </Form.Label>
        <Col md='9'>
          <Form.Control
            as='select'
            // className='custom-select'
            value={props.default}
            onChange={props.setDefault}
            disabled={disabledAssignFtg
              || props.groups.length <= 0}
          >
            <option value='' disabled={true}>-- please select --</option>
            {renderDefaultOptions()}
          </Form.Control>
          <Form.Text>
            Set one of the assigned feature type groups as the default group for the feature type.
          </Form.Text>
        </Col>
      </Form.Group>
    </React.Fragment>
  );
}

export default AssignToFeatureTypeGroups;